// Present-spec rows for a listing variant (plan T12). Renders only the spec
// fields that are actually present — absent / blank fields are omitted, and an
// empty spec sheet (the common case: ~96.6% of variants, all Smartphones)
// renders nothing at all, so the spec block cleanly disappears.
//
// Pure + presentational so it unit-tests directly with a plain specs object.

import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';

interface Props {
  specs: Record<string, unknown>;
}

/** "screen_size" → "Screen Size"; "os" → "Os". */
export function humanizeSpecKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/** Render a spec value to a display string, or null when it carries nothing. */
export function formatSpecValue(value: unknown): string | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const parts = value.map((v) => String(v).trim()).filter((v) => v.length > 0);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  const str = String(value).trim();
  return str.length > 0 ? str : null;
}

export default function ListingSpecList({ specs }: Props) {
  const theme = useTheme();

  const rows = useMemo(
    () =>
      Object.entries(specs)
        .map(([key, value]) => ({ key, label: humanizeSpecKey(key), value: formatSpecValue(value) }))
        .filter((row): row is { key: string; label: string; value: string } => row.value !== null),
    [specs],
  );

  const styles = useMemo(
    () =>
      StyleSheet.create({
        block: { marginTop: theme.spacing.lg },
        heading: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          marginBottom: theme.spacing.sm,
        },
        row: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          paddingVertical: theme.spacing.sm,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          gap: theme.spacing.md,
        },
        label: { ...theme.typography.sm, color: theme.colors.textSecondary },
        value: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          flexShrink: 1,
          textAlign: 'right',
        },
      }),
    [theme],
  );

  // No present spec fields → render nothing (the spec block cleanly omits).
  if (rows.length === 0) return null;

  return (
    <View style={styles.block}>
      <Text style={styles.heading}>Specifications</Text>
      {rows.map((row) => (
        <View key={row.key} style={styles.row}>
          <Text style={styles.label}>{row.label}</Text>
          <Text style={styles.value}>{row.value}</Text>
        </View>
      ))}
    </View>
  );
}
